import { Dialog } from "../Dialog";
import { useApp, useSchemadrawSetAppState } from "../App";
import MermaidToSchemadraw from "./MermaidToSchemadraw";
import TTDDialogTabs from "./TTDDialogTabs";
import type { ChangeEventHandler } from "react";
import { useEffect, useRef, useState } from "react";
import { useUIAppState } from "../../../context/ui-appState";
import { withInternalFallback } from "../hoc/withInternalFallback";
import { TTDDialogTabTriggers } from "./TTDDialogTabTriggers";
import { TTDDialogTabTrigger } from "./TTDDialogTabTrigger";
import { TTDDialogTab } from "./TTDDialogTab";
import { t } from "../../../utils/i18n";
import { TTDDialogInput } from "./TTDDialogInput";
import { TTDDialogOutput } from "./TTDDialogOutput";
import { TTDDialogPanel } from "./TTDDialogPanel";
import { TTDDialogPanels } from "./TTDDialogPanels";
import type { MermaidToSchemadrawLibProps } from "./common";
import {
  convertMermaidToSchemadraw,
  insertToEditor,
  saveMermaidDataToStorage,
} from "./common";
import type { NonDeletedSchemadrawElement } from "../../../element/types";
import type { BinaryFiles } from "../../../utils/types";
import { ArrowRightIcon } from "../icons";

import "./TTDDialog.scss";
import { atom, useAtom } from "jotai";
import { InlineIcon } from "../InlineIcon";
import { TTDDialogSubmitShortcut } from "./TTDDialogSubmitShortcut";
import { isFiniteNumber } from "../../../math";
import Trans from "../Trans";


const MIN_PROMPT_LENGTH = 3;
const MAX_PROMPT_LENGTH = 1000;

const rateLimitsAtom = atom<{
  rateLimit: number;
  rateUsed: number;
} | null>(null);

const ttdGenerationAtom = atom<{
  generatedResponse: string | null;
  prompt: string | null;
} | null>(null);

type OnTestSubmitRetValue = {
  rateLimit?: number | null;
  rateUsed?: number | null;
} & (
    | { generatedResponse: string | undefined; error?: null | undefined }
    | {
      error: Error;
      generatedResponse?: null | undefined;
    }
  );

export const TTDDialog = (
  props:
    | {
      onTextSubmit(value: string): Promise<OnTestSubmitRetValue>;
    }
    | { __fallback: true; },
) => {
  const appState = useUIAppState();

  if (appState.openDialog?.name !== "ttd") {
    return null;
  }

  return <TTDDialogBase {...props} tab={appState.openDialog.tab} />;
};

/**
 * Text to diagram (TTD) dialog
 */
export const TTDDialogBase = withInternalFallback(
  "TTDDialogBase",
  ({
    tab,
    ...rest
  }: {
    tab: "text-to-diagram" | "mermaid";
  } & (
      | {
        onTextSubmit(value: string): Promise<OnTestSubmitRetValue>;
      }
      | { __fallback: true }
    )) => {
    const app = useApp();
    const setAppState = useSchemadrawSetAppState();

    const someRandomDivRef = useRef<HTMLDivElement>(null);

    const [ttdGeneration, setTtdGeneration] = useAtom(ttdGenerationAtom);

    const [text, setText] = useState(ttdGeneration?.prompt ?? "");

    const prompt = text.trim();

    const handleTextChange: ChangeEventHandler<HTMLTextAreaElement> = (
      event,
    ) => {
      setText(event.target.value);
      setTtdGeneration((s) => ({
        generatedResponse: s?.generatedResponse ?? null,
        prompt: event.target.value,
      }));
    };

    const [onTextSubmitInProgess, setOnTextSubmitInProgess] = useState(false);
    const [rateLimits, setRateLimits] = useAtom(rateLimitsAtom);

    const onGenerate = async () => {
      if (
        prompt.length > MAX_PROMPT_LENGTH ||
        prompt.length < MIN_PROMPT_LENGTH ||
        onTextSubmitInProgess ||
        (rateLimits !== null && rateLimits?.rateUsed === rateLimits?.rateLimit) ||
        // means this is not a text-to-diagram dialog (needed for TS only)
        "__fallback" in rest
      ) {
        if (prompt.length < MIN_PROMPT_LENGTH) {
          setError(
            new Error(
              `Prompt is too short (min ${MIN_PROMPT_LENGTH} characters)`,
            ),
          );
        }
        if (prompt.length > MAX_PROMPT_LENGTH) {
          setError(
            new Error(
              `Prompt is too long (max ${MAX_PROMPT_LENGTH} characters)`,
            ),
          );
        }

        return;
      }

      try {
        setOnTextSubmitInProgess(true);

        const { generatedResponse, error, rateLimit, rateUsed } =
          await rest.onTextSubmit(prompt);

        if (typeof generatedResponse === "string") {
          setTtdGeneration((s) => ({
            generatedResponse,
            prompt: s?.prompt ?? null,
          }));
        }

        if (isFiniteNumber(rateLimit) && isFiniteNumber(rateUsed)) {
          setRateLimits({ rateLimit, rateUsed });
        }

        if (error) {
          setError(error);
          return;
        }
        if (!generatedResponse) {
          setError(new Error("Generation failed"));
          return;
        }

        try {
          await convertMermaidToSchemadraw({
            canvasRef: someRandomDivRef,
            data,
            mermaidToSchemadrawLib,
            setError,
            mermaidDefinition: generatedResponse,
          });
        } catch (error: any) {
          console.info(
            `%cTTD mermaid render errror: ${error.message}`,
            "color: red",
          );
          console.info(
            `>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>\nTTD mermaid definition render errror: ${error.message}`,
            "color: yellow",
          );
          setError(
            new Error(
              "Generated an invalid diagram :(. You may also try a different prompt.",
            ),
          );
        }
      } catch (error: any) {
        let message: string | undefined = error.message;
        if (!message || message === "Failed to fetch") {
          message = "Request failed";
        }
        setError(new Error(message));
      } finally {
        setOnTextSubmitInProgess(false);
      }
    };

    const refOnGenerate = useRef(onGenerate);
    refOnGenerate.current = onGenerate;

    const [mermaidToSchemadrawLib, setMermaidToSchemadrawLib] =
      useState<MermaidToSchemadrawLibProps>({
        loaded: false,
        api: import("@excalidraw/mermaid-to-excalidraw"),
      });

    useEffect(() => {
      const fn = async () => {
        await mermaidToSchemadrawLib.api;
        setMermaidToSchemadrawLib((prev) => ({ ...prev, loaded: true }));
      };
      fn();
    }, [mermaidToSchemadrawLib.api]);

    const data = useRef<{
      elements: readonly NonDeletedSchemadrawElement[];
      files: BinaryFiles | null;
    }>({ elements: [], files: null });

    const [error, setError] = useState<Error | null>(null);

    return (
      <Dialog
        className="ttd-dialog"
        onCloseRequest={() => {
          app.setOpenDialog(null);
        }}
        size={1200}
        title={false}
        {...rest}
        autofocus={false}
      >
        <TTDDialogTabs dialog="ttd" tab={tab}>
          <TTDDialogTabTriggers>
            <TTDDialogTabTrigger tab="text-to-diagram">
              <div style={{ display: "flex", alignItems: "center" }}>
                {t("labels.textToDiagram")}
              </div>
            </TTDDialogTabTrigger>
            <TTDDialogTabTrigger tab="mermaid">
              {t("toolBar.mermaidToSchemadraw")}
            </TTDDialogTabTrigger>
          </TTDDialogTabTriggers>

          <TTDDialogTab className="ttd-dialog-content" tab="mermaid">
            <MermaidToSchemadraw
              mermaidToSchemadrawLib={mermaidToSchemadrawLib}
            />
          </TTDDialogTab>
          <TTDDialogTab className="ttd-dialog-content" tab="text-to-diagram">
            <div className="ttd-dialog-desc">
              {t("mermaid.tip")}
            </div>
            <div className="ttd-dialog-example">
              <Trans
                i18nKey="mermaid.example"
                bold={(text) => <strong>{text}</strong>}
                br={() => <br />}
              />
            </div>
            <TTDDialogPanels>
              <TTDDialogPanel
                label={t("labels.prompt")}
                panelAction={{
                  action: onGenerate,
                  label: "Generate",
                  icon: ArrowRightIcon,
                }}
                onTextSubmitInProgess={onTextSubmitInProgess}
                panelActionDisabled={
                  prompt.length > MAX_PROMPT_LENGTH ||
                  (rateLimits !== null && rateLimits?.rateUsed === rateLimits?.rateLimit)
                }
                renderTopRight={() => {
                  if (!rateLimits) {
                    return null;
                  }

                  return (
                    <div
                      className="ttd-dialog-rate-limit"
                      style={{
                        fontSize: 12,
                        marginLeft: "auto",
                        color:
                          (rateLimits !== null && rateLimits.rateUsed === rateLimits.rateLimit)
                            ? "var(--color-danger)"
                            : undefined,
                      }}
                    >
                      {t("mermaid.requestLimit")} {rateLimits.rateUsed} / {rateLimits.rateLimit}
                    </div>
                  );
                }}
                renderSubmitShortcut={() => <TTDDialogSubmitShortcut />}
                renderBottomRight={() => {
                  if (typeof ttdGeneration?.generatedResponse === "string") {
                    return (
                      <div
                        className="schemadraw-link"
                        style={{ marginLeft: "auto", fontSize: 14 }}
                        onClick={() => {
                          if (
                            typeof ttdGeneration?.generatedResponse ===
                            "string"
                          ) {
                            saveMermaidDataToStorage(
                              ttdGeneration.generatedResponse,
                            );
                            setAppState({
                              openDialog: { name: "ttd", tab: "mermaid" },
                            });
                          }
                        }}
                      >
                        {t("mermaid.viewMermaid")}
                        <InlineIcon icon={ArrowRightIcon} />
                      </div>
                    );
                  }
                  const ratio = prompt.length / MAX_PROMPT_LENGTH;
                  if (ratio > 0.8) {
                    return (
                      <div
                        style={{
                          marginLeft: "auto",
                          fontSize: 12,
                          fontFamily: "monospace",
                          color:
                            ratio > 1 ? "var(--color-danger)" : undefined,
                        }}
                      >
                        Length: {prompt.length}/{MAX_PROMPT_LENGTH}
                      </div>
                    );
                  }

                  return null;
                }}
              >
                <TTDDialogInput
                  onChange={handleTextChange}
                  input={text}
                  placeholder={t("mermaid.placeholder")}
                  onKeyboardSubmit={() => {
                    refOnGenerate.current();
                  }}
                />
              </TTDDialogPanel>
              <TTDDialogPanel
                label="Preview"
                panelAction={{
                  action: () => {
                    console.info("Panel action clicked");
                    insertToEditor({ app, data });
                  },
                  label: "Insert",
                  icon: ArrowRightIcon,
                }}
              >
                <TTDDialogOutput
                  canvasRef={someRandomDivRef}
                  error={error}
                  loaded={mermaidToSchemadrawLib.loaded}
                />
              </TTDDialogPanel>
            </TTDDialogPanels>
          </TTDDialogTab>
        </TTDDialogTabs>
      </Dialog>
    );
  },
);
